import { useCallback, useContext, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { Loading, TablePagination } from '../components/utility/Utility';
import { ReactComponent as ImagePlaceholderIcon } from '../assets/images/icons/image-placeholder.svg';
import '../assets/css/views/settings.css';
import '../assets/css/views/ai-analysis.css';
import {
  buildSharedWordHints,
  createSearchGroup,
  filterValuesBySearchQuery,
  hasActiveSearchQuery,
  tokenizeTagEntry
} from '../utils/aiAnalysisStandardizations';

axios.defaults.withCredentials = true;

const API = 'https://api.amplifyloyalty.com';

const TABS = [
  { id: 'products', label: 'Products' },
  { id: 'standardizations', label: 'Standardizations' }
];

const PRODUCT_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'success', label: 'Analyzed' },
  { id: 'failed', label: 'Failed' },
  { id: 'pending', label: 'In progress' },
  { id: 'none', label: 'Not analyzed' }
];

const STD_FIELDS = [
  { id: 'subjects', label: 'Subjects' },
  { id: 'themes', label: 'Themes' },
  { id: 'design_styles', label: 'Design styles' },
  { id: 'colors', label: 'Colors' }
];

function createTagSearchGroupState(orTerms = []) {
  const base = createSearchGroup([]);
  return { id: base.id, orTerms: [...orTerms], draft: '' };
}

function buildQueryFromTagSearchGroups(groups) {
  return {
    andGroups: (groups || []).map(({ id, orTerms, draft }) => {
      const terms = [...orTerms];
      const d = String(draft || '').trim();
      if (d && !terms.some((t) => t.toLowerCase() === d.toLowerCase())) {
        terms.push(d);
      }
      return { id, orTerms: terms.length ? terms : [''] };
    })
  };
}

function aiStatusPillClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'success') return 'ai-analysis__status-pill--success';
  if (s === 'failed') return 'ai-analysis__status-pill--failed';
  if (s === 'pending') return 'ai-analysis__status-pill--pending';
  return 'ai-analysis__status-pill--none';
}

function aiStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'success') return 'Analyzed';
  if (s === 'failed') return 'Failed';
  if (s === 'pending') return 'In progress';
  return 'Not analyzed';
}

export default function AiAnalysis() {
  const { getJwtToken, userProfile, canWrite } = useContext(AuthContext);
  const storeName = userProfile?.store?.settings?.name || '';

  const [tab, setTab] = useState('products');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activeCatalogRun, setActiveCatalogRun] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [products, setProducts] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productStatus, setProductStatus] = useState('all');
  const [productPage, setProductPage] = useState(1);
  const [productPagination, setProductPagination] = useState(false);
  const lastProductQuery = useRef('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [stdField, setStdField] = useState('subjects');
  const [stdTagsLoading, setStdTagsLoading] = useState(false);
  const [attributeValues, setAttributeValues] = useState([]);
  const [stdApplyLoading, setStdApplyLoading] = useState(false);
  const [tagActionMode, setTagActionMode] = useState('replace');
  const [tagSearchGroups, setTagSearchGroups] = useState(() => [createTagSearchGroupState()]);
  const [replacePool, setReplacePool] = useState([]);
  const [replaceCanonical, setReplaceCanonical] = useState('');
  const [splitPool, setSplitPool] = useState([]);
  const [splitReplacements, setSplitReplacements] = useState(['', '']);
  const tagSearchSectionRef = useRef(null);

  const [bulkLimit, setBulkLimit] = useState('');
  const [force, setForce] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getJwtToken();
    if (!token) throw new Error('Missing auth token.');
    return { Authorization: `Bearer ${token}` };
  }, [getJwtToken]);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API}/app/product-analysis/ai/status`, { headers });
      setActiveCatalogRun(res.data?.active ? res.data.run : null);
    } catch {
      // best-effort
    } finally {
      setStatusLoading(false);
    }
  }, [authHeaders]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const headers = await authHeaders();
      const res = await axios.get(`${API}/app/product-analysis/ai/summary`, { headers });
      setSummary(res.data);
    } catch (e) {
      setSummary(null);
      setError(e?.response?.data?.message || e?.message || 'Failed to load summary.');
    } finally {
      setSummaryLoading(false);
    }
  }, [authHeaders]);

  const loadProducts = useCallback(async () => {
    setProducts(false);
    try {
      const headers = await authHeaders();
      const params = {
        page: productPage,
        query: productQuery,
        sort: JSON.stringify({ title: 1 })
      };
      if (productStatus !== 'all') params.status = productStatus;
      const res = await axios.get(`${API}/app/product-analysis/ai/products`, { headers, params });
      const info = { ...res.data };
      delete info.docs;
      setProductPagination(info);
      setProducts(res.data.docs || []);
    } catch (e) {
      setProducts([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to load products.');
    }
  }, [authHeaders, productPage, productQuery, productStatus]);

  const loadStdTags = useCallback(async () => {
    setStdTagsLoading(true);
    try {
      const headers = await authHeaders();
      const valuesRes = await axios.get(`${API}/app/product-analysis/ai/attribute-values`, {
        headers,
        params: { field: stdField }
      });
      setAttributeValues(valuesRes.data?.values || []);
    } catch (e) {
      setAttributeValues([]);
      setError(e?.response?.data?.message || e?.message || 'Failed to load tags.');
    } finally {
      setStdTagsLoading(false);
    }
  }, [authHeaders, stdField]);

  const resetStandardizationsField = useCallback(() => {
    setTagSearchGroups([createTagSearchGroupState()]);
    setReplacePool([]);
    setReplaceCanonical('');
    setSplitPool([]);
    setSplitReplacements(['', '']);
    setTagActionMode('replace');
  }, []);

  const reloadStandardizations = useCallback(() => {
    resetStandardizationsField();
    loadStdTags();
  }, [resetStandardizationsField, loadStdTags]);

  useEffect(() => {
    if (!userProfile) return;
    loadSummary();
    refreshStatus();
  }, [userProfile?.store?._id, loadSummary, refreshStatus]);

  useEffect(() => {
    if (tab !== 'products') return;
    loadProducts();
  }, [tab, loadProducts]);

  useEffect(() => {
    if (tab !== 'standardizations') return;
    resetStandardizationsField();
    loadStdTags();
  }, [tab, stdField, resetStandardizationsField, loadStdTags]);

  const sharedWordHints = useMemo(() => {
    if (stdField === 'colors') return [];
    return buildSharedWordHints(attributeValues, { minVariations: 3 });
  }, [attributeValues, stdField]);

  const tagSearchQuery = useMemo(
    () => buildQueryFromTagSearchGroups(tagSearchGroups),
    [tagSearchGroups]
  );

  const tagSearchMatches = useMemo(
    () => filterValuesBySearchQuery(attributeValues, tagSearchQuery),
    [attributeValues, tagSearchQuery]
  );

  const applyOneGroup = async (group) => {
    const headers = await authHeaders();
    return axios.post(
      `${API}/app/product-analysis/ai/standardizations/apply`,
      { field: stdField, groups: [group] },
      { headers }
    );
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (productSearch !== lastProductQuery.current) {
        lastProductQuery.current = productSearch;
        setProductQuery(productSearch);
        setProductPage(1);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [productSearch]);

  const runEnqueueAll = async () => {
    const limitNum = bulkLimit ? Number(String(bulkLimit).trim()) : null;
    if (bulkLimit && (!Number.isFinite(limitNum) || limitNum <= 0)) {
      setError('Limit must be a positive number (or leave blank).');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Enqueue AI analysis for the full catalog${bulkLimit ? ` (limit ${Math.floor(limitNum)})` : ''}?`)) {
      return;
    }
    setActionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const headers = await authHeaders();
      await axios.post(
        `${API}/app/product-analysis/ai/enqueue-all`,
        { force, ...(bulkLimit ? { limit: Math.floor(limitNum) } : {}) },
        { headers }
      );
      setSuccessMsg('Catalog analysis enqueued.');
      await refreshStatus();
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Enqueue failed.');
      if (e?.response?.status === 409) await refreshStatus();
    } finally {
      setActionLoading(false);
    }
  };

  const runEnqueueFailed = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Re-analyze all products with failed AI analysis?')) return;
    setActionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const headers = await authHeaders();
      const res = await axios.post(`${API}/app/product-analysis/ai/enqueue-failed`, {}, { headers });
      setSuccessMsg(`Enqueued ${res.data?.enqueued ?? 0} failed product(s) for re-analysis.`);
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Enqueue failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const rerunProduct = async (shopifyProductId) => {
    setActionLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      await axios.post(
        `${API}/app/product-analysis/ai/enqueue`,
        { shopify_product_id: shopifyProductId, force: true },
        { headers }
      );
      setSuccessMsg(`Re-analysis enqueued for product ${shopifyProductId}.`);
      await loadProducts();
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Re-run failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const rerunSelectedFailed = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setActionLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      await Promise.all(
        ids.map((pid) =>
          axios.post(
            `${API}/app/product-analysis/ai/enqueue`,
            { shopify_product_id: pid, force: true },
            { headers }
          )
        )
      );
      setSuccessMsg(`Enqueued ${ids.length} product(s).`);
      setSelectedIds(new Set());
      await loadProducts();
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Bulk re-run failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const searchTagsFromHint = (word) => {
    setTagSearchGroups([createTagSearchGroupState([word])]);
    tagSearchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateTagSearchDraft = (groupId, value) => {
    setTagSearchGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, draft: value } : g))
    );
  };

  const addTagSearchOrTerm = (groupId) => {
    setTagSearchGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const term = String(g.draft || '').trim();
        if (!term) return g;
        if (g.orTerms.some((t) => t.toLowerCase() === term.toLowerCase())) {
          return { ...g, draft: '' };
        }
        return { ...g, orTerms: [...g.orTerms, term], draft: '' };
      })
    );
  };

  const removeTagSearchOrTerm = (groupId, termIndex) => {
    setTagSearchGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, orTerms: g.orTerms.filter((_, i) => i !== termIndex) }
          : g
      )
    );
  };

  const addTagSearchAndGroup = () => {
    setTagSearchGroups((prev) => [...prev, createTagSearchGroupState()]);
  };

  const removeTagSearchAndGroup = (groupId) => {
    setTagSearchGroups((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((g) => g.id !== groupId);
    });
  };

  const isInReplacePool = (value) => replacePool.includes(value);
  const isInSplitPool = (key) => splitPool.some((t) => t.key === key);

  const selectTagResult = (v) => {
    if (tagActionMode === 'split') {
      if (isInSplitPool(v.key)) return;
      setSplitPool((prev) => [...prev, { key: v.key, value: v.value, product_count: v.product_count ?? 0 }]);
      return;
    }
    if (isInReplacePool(v.value)) return;
    setReplacePool((prev) => [...prev, v.value]);
  };

  const removeFromReplacePool = (value) => {
    setReplacePool((prev) => prev.filter((x) => x !== value));
  };

  const removeFromSplitPool = (key) => {
    setSplitPool((prev) => prev.filter((t) => t.key !== key));
  };

  const applyReplace = async () => {
    const canonical = String(replaceCanonical || '').trim();
    const aliases = replacePool.filter((v) => v && v !== canonical);
    if (!canonical) {
      setError('Enter a replacement canonical label.');
      return;
    }
    if (!aliases.length) {
      setError('Select at least one tag for the replace pool (other than the canonical).');
      return;
    }
    setStdApplyLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await applyOneGroup({
        canonical,
        aliases,
        members: aliases.map((value) => ({
          value,
          product_count: attributeValues.find((a) => a.value === value)?.product_count ?? 0
        }))
      });
      setSuccessMsg(
        `Replaced with "${canonical}" — updated ${res.data?.products_updated ?? 0} product(s).`
      );
      setReplacePool([]);
      setReplaceCanonical('');
      await loadStdTags();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Replace failed.');
    } finally {
      setStdApplyLoading(false);
    }
  };

  const autoSplitFill = () => {
    const first = splitPool[0]?.value;
    if (!first) return;
    const tokens = tokenizeTagEntry(first);
    setSplitReplacements(tokens.length >= 2 ? tokens : tokens.length === 1 ? [tokens[0], ''] : ['', '']);
  };

  const applySplitPool = async () => {
    if (!splitPool.length) return;
    const replacements = splitReplacements.map((s) => String(s || '').trim()).filter(Boolean);
    if (replacements.length < 1) {
      setError('Enter at least one replacement tag.');
      return;
    }
    setStdApplyLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const headers = await authHeaders();
      let totalUpdated = 0;
      for (const tag of splitPool) {
        const res = await axios.post(
          `${API}/app/product-analysis/ai/standardizations/split`,
          { field: stdField, source: tag.value, replacements },
          { headers }
        );
        totalUpdated += res.data?.products_updated ?? 0;
      }
      setSuccessMsg(`Split ${splitPool.length} tag(s) — updated ${totalUpdated} product(s).`);
      setSplitPool([]);
      setSplitReplacements(['', '']);
      await loadStdTags();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Split failed.');
    } finally {
      setStdApplyLoading(false);
    }
  };

  const changeStdField = (fieldId) => {
    setStdField(fieldId);
  };

  const toggleSelect = (pid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  if (!userProfile) return <Loading />;

  const total = summary?.total ?? 0;
  const analyzed = summary?.success ?? 0;
  const pct = total > 0 ? Math.round((analyzed / total) * 100) : 0;

  return (
    <div className="main">
      <div className="settings ai-analysis">
        <div className="ai-analysis__header">
          <div>
            <h2>AI Analysis</h2>
            <p className="ai-analysis__sub">
              Manage product vision analysis for {storeName || 'your store'}.
            </p>
          </div>
          <button
            type="button"
            className="ai-analysis__btn ai-analysis__btn--secondary"
            onClick={() => {
              refreshStatus();
              loadSummary();
            }}
            disabled={statusLoading}
          >
            {statusLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {!canWrite && (
          <p className="settings__sub-heading" style={{ marginBottom: '1rem' }}>
            You have read-only access. Contact your store owner to run analysis or apply merges.
          </p>
        )}

        <nav className="ai-analysis__tabs" aria-label="AI Analysis sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`ai-analysis__tab${tab === t.id ? ' ai-analysis__tab--active' : ''}`}
              onClick={() => {
                setTab(t.id);
                setError('');
                setSuccessMsg('');
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {error ? <p className="ai-analysis__msg ai-analysis__msg--error">{error}</p> : null}
        {successMsg ? <p className="ai-analysis__msg ai-analysis__msg--success">{successMsg}</p> : null}

        {tab === 'products' && (
          <div className="ai-analysis__panel">
            <section className="ai-analysis__overview-section" aria-label="Analysis overview">
              {summaryLoading ? (
                <div className="ai-analysis__inline-loading">
                  <Loading className="ai-analysis__section-spinner" />
                  <span>Loading overview…</span>
                </div>
              ) : (
                <>
                  <div className="ai-analysis__stats">
                    <div className="ai-analysis__stat ai-analysis__stat--success">
                      <div className="ai-analysis__stat-value">{summary?.success ?? 0}</div>
                      <div className="ai-analysis__stat-label">Analyzed</div>
                    </div>
                    <div className="ai-analysis__stat ai-analysis__stat--failed">
                      <div className="ai-analysis__stat-value">{summary?.failed ?? 0}</div>
                      <div className="ai-analysis__stat-label">Failed</div>
                    </div>
                    <div className="ai-analysis__stat ai-analysis__stat--pending">
                      <div className="ai-analysis__stat-value">{summary?.pending ?? 0}</div>
                      <div className="ai-analysis__stat-label">In progress</div>
                    </div>
                    <div className="ai-analysis__stat">
                      <div className="ai-analysis__stat-value">{summary?.not_analyzed ?? 0}</div>
                      <div className="ai-analysis__stat-label">Not analyzed</div>
                    </div>
                    <div className="ai-analysis__stat">
                      <div className="ai-analysis__stat-value">{total}</div>
                      <div className="ai-analysis__stat-label">Total products</div>
                    </div>
                  </div>

                  <div className="ai-analysis__progress">
                    <div style={{ fontSize: '0.875rem' }}>
                      <strong>{pct}%</strong> of catalog analyzed ({analyzed} / {total})
                    </div>
                    <div className="ai-analysis__progress-bar">
                      <div className="ai-analysis__progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  {activeCatalogRun ? (
                    <div className="ai-analysis__banner">
                      <strong>Catalog run in progress</strong>
                      <div>
                        Run: <code>{activeCatalogRun.runId}</code>
                      </div>
                      <div>
                        Enqueued: {activeCatalogRun.enqueued ?? 0} • Scanned: {activeCatalogRun.scanned ?? 0}
                      </div>
                    </div>
                  ) : null}

                  {/* <div className="ai-analysis__actions">
                    <button
                      type="button"
                      className="ai-analysis__btn"
                      disabled={!canWrite || actionLoading || Boolean(activeCatalogRun)}
                      onClick={runEnqueueAll}
                    >
                      Analyze full catalog
                    </button>
                    <button
                      type="button"
                      className="ai-analysis__btn ai-analysis__btn--secondary"
                      disabled={!canWrite || actionLoading || !(summary?.failed > 0)}
                      onClick={runEnqueueFailed}
                    >
                      Re-analyze all failed
                    </button>
                    <label style={{ fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={force}
                        onChange={(e) => setForce(e.target.checked)}
                        disabled={!canWrite}
                      />
                      Force re-run
                    </label>
                    <input
                      type="text"
                      placeholder="Optional limit (e.g. 500)"
                      value={bulkLimit}
                      onChange={(e) => setBulkLimit(e.target.value)}
                      style={{ width: 140, padding: '0.4rem 0.5rem', borderRadius: 6, border: '1px solid #ddd' }}
                      disabled={!canWrite}
                    />
                  </div> */}
                </>
              )}
            </section>

            <section className="ai-analysis__products-section" aria-label="Product search">
              <input
                type="search"
                className="ai-analysis__search"
                placeholder="Search by title, handle, SKU, or product ID"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
              <div className="ai-analysis__filters">
                {PRODUCT_STATUS_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`ai-analysis__chip${productStatus === f.id ? ' ai-analysis__chip--active' : ''}`}
                    onClick={() => {
                      setProductStatus(f.id);
                      setProductPage(1);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {selectedIds.size > 0 && productStatus === 'failed' && (
                <div className="ai-analysis__actions" style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    className="ai-analysis__btn"
                    disabled={!canWrite || actionLoading}
                    onClick={rerunSelectedFailed}
                  >
                    Re-run selected ({selectedIds.size})
                  </button>
                </div>
              )}
              {!products ? (
                <div className="ai-analysis__inline-loading">
                  <Loading className="ai-analysis__section-spinner" />
                  <span>Loading products…</span>
                </div>
              ) : products.length === 0 ? (
                <div className="ai-analysis__empty">No products match this filter.</div>
              ) : (
                <>
                  <div className="ai-analysis__table-wrap">
                    <table className="ai-analysis__table">
                      <thead>
                        <tr>
                          {productStatus === 'failed' && <th style={{ width: 36 }} />}
                          <th style={{ width: 48 }} />
                          <th>Product</th>
                          <th>AI status</th>
                          <th>Analyzed</th>
                          <th>Error</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((p) => (
                          <tr key={p.shopify_product_id}>
                            {productStatus === 'failed' && (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(p.shopify_product_id)}
                                  onChange={() => toggleSelect(p.shopify_product_id)}
                                  disabled={!canWrite}
                                />
                              </td>
                            )}
                            <td>
                              {p.image_src ? (
                                <img src={p.image_src} alt="" className="ai-analysis__thumb" />
                              ) : (
                                <ImagePlaceholderIcon className="ai-analysis__thumb" aria-hidden />
                              )}
                            </td>
                            <td>{p.title || `Product ${p.shopify_product_id}`}</td>
                            <td>
                              <span
                                className={`ai-analysis__status-pill ${aiStatusPillClass(p.ai_analysis_status)}`}
                              >
                                {aiStatusLabel(p.ai_analysis_status)}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.75rem', color: '#666' }}>
                              {p.ai_analyzed_at
                                ? new Date(p.ai_analyzed_at).toLocaleString()
                                : '—'}
                            </td>
                            <td className="ai-analysis__error-cell" title={p.ai_error || ''}>
                              {p.ai_error || '—'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="ai-analysis__btn ai-analysis__btn--secondary"
                                style={{ padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}
                                disabled={!canWrite || actionLoading}
                                onClick={() => rerunProduct(p.shopify_product_id)}
                              >
                                Re-run
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {productPagination && (
                    <TablePagination
                      options={productPagination}
                      docType="products"
                      previous={() => setProductPage(productPagination.prevPage)}
                      next={() => setProductPage(productPagination.nextPage)}
                    />
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {tab === 'standardizations' && (
          <div className="ai-analysis__panel">
            <p className="ai-analysis__sub" style={{ marginBottom: '1rem' }}>
              Use shared-word suggestions to find overlapping tags, then combine or split them. The word map
              below supports quick one-off canonical edits.
            </p>
            <div className="ai-analysis__field-tabs">
              {STD_FIELDS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`ai-analysis__chip${stdField === f.id ? ' ai-analysis__chip--active' : ''}`}
                  onClick={() => changeStdField(f.id)}
                >
                  {f.label}
                </button>
              ))}
              <button
                type="button"
                className="ai-analysis__btn ai-analysis__btn--secondary"
                style={{ marginLeft: 'auto', padding: '0.35rem 0.75rem', fontSize: '0.75rem' }}
                disabled={stdTagsLoading}
                onClick={reloadStandardizations}
              >
                Refresh
              </button>
            </div>

            {stdField !== 'colors' && (
              <section className="ai-analysis__suggestions-panel" aria-label="Shared word suggestions">
                <div className="ai-analysis__suggestions-panel-head">
                  <h3 className="ai-analysis__suggestions-title">
                    Suggestions
                    {!stdTagsLoading && (
                      <span className="ai-analysis__suggestions-badge">{sharedWordHints.length}</span>
                    )}
                  </h3>
                </div>
                {stdTagsLoading ? (
                  <div className="ai-analysis__inline-loading">
                    <Loading className="ai-analysis__section-spinner" />
                    <span>Loading…</span>
                  </div>
                ) : sharedWordHints.length === 0 ? (
                  <div className="ai-analysis__empty">
                    No shared words with 3+ tag variations yet for this field.
                  </div>
                ) : (
                  <div className="ai-analysis__hint-list">
                    {sharedWordHints.slice(0, 50).map((hint) => (
                      <div key={hint.word} className="ai-analysis__hint-row">
                        <div className="ai-analysis__hint-row-text">
                          <strong>{hint.word}</strong>
                          <span className="ai-analysis__hint-row-meta">
                            {hint.variation_count} variation{hint.variation_count === 1 ? '' : 's'}
                            {' · '}
                            ~{hint.product_count} product{hint.product_count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="ai-analysis__btn ai-analysis__btn--secondary ai-analysis__hint-search-btn"
                          onClick={() => searchTagsFromHint(hint.word)}
                        >
                          Search tags
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section
              ref={tagSearchSectionRef}
              className="ai-analysis__combine-section"
              aria-label="Tag search"
            >
              <div className="ai-analysis__tag-search-header">
                <h3 className="ai-analysis__word-map-title">Tag search</h3>
                <div className="ai-analysis__tag-action-mode-wrap">
                  <span className="ai-analysis__tag-action-mode-label">TYPE</span>
                  <div className="ai-analysis__tag-action-mode" role="group" aria-label="Tag action mode">
                    <button
                      type="button"
                      className={`ai-analysis__tag-mode-btn${tagActionMode === 'replace' ? ' ai-analysis__tag-mode-btn--active' : ''}`}
                      onClick={() => setTagActionMode('replace')}
                    >
                      Replace
                    </button>
                    {stdField !== 'colors' && (
                      <button
                        type="button"
                        className={`ai-analysis__tag-mode-btn${tagActionMode === 'split' ? ' ai-analysis__tag-mode-btn--active' : ''}`}
                        onClick={() => setTagActionMode('split')}
                      >
                        Split
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <p className="ai-analysis__sub" style={{ marginBottom: '0.75rem' }}>
                {tagActionMode === 'replace'
                  ? 'Build an AND/OR search, select tags, and merge them into one canonical label.'
                  : 'Build an AND/OR search, select compound tags, and split each into atomic replacements.'}
              </p>

              <div className="ai-analysis__tag-search-builder">
                {tagSearchGroups.map((group, groupIndex) => (
                  <Fragment key={group.id}>
                    {groupIndex > 0 && (
                      <div className="ai-analysis__tag-search-and-divider" aria-hidden="true">
                        AND
                      </div>
                    )}
                    <div className="ai-analysis__tag-search-group">
                      <div className="ai-analysis__tag-search-input-group">
                        <input
                          type="text"
                          className="ai-analysis__tag-search-input"
                          placeholder="Search term…"
                          value={group.draft}
                          onChange={(e) => updateTagSearchDraft(group.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') addTagSearchOrTerm(group.id);
                          }}
                          aria-label={
                            groupIndex === 0 ? 'Tag search term' : `Tag search term (group ${groupIndex + 1})`
                          }
                        />
                        {groupIndex === 0 ? (
                          <button
                            type="button"
                            className="ai-analysis__tag-search-add"
                            onClick={() => addTagSearchOrTerm(group.id)}
                          >
                            ADD
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ai-analysis__tag-search-remove-group"
                            aria-label="Remove AND condition"
                            onClick={() => removeTagSearchAndGroup(group.id)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {group.orTerms.length > 0 && (
                        <div className="ai-analysis__keyword-pills ai-analysis__tag-search-or-pills">
                          {group.orTerms.map((term, termIndex) => (
                            <span key={`${group.id}-${term}-${termIndex}`} className="ai-analysis__keyword-pill">
                              {term}
                              <button
                                type="button"
                                className="ai-analysis__keyword-pill-remove"
                                aria-label={`Remove ${term}`}
                                onClick={() => removeTagSearchOrTerm(group.id, termIndex)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {groupIndex === tagSearchGroups.length - 1 && (
                        <button
                          type="button"
                          className="ai-analysis__tag-search-and-btn"
                          onClick={addTagSearchAndGroup}
                        >
                          AND
                        </button>
                      )}
                    </div>
                  </Fragment>
                ))}
              </div>

              {stdTagsLoading ? (
                <div className="ai-analysis__inline-loading">
                  <Loading className="ai-analysis__section-spinner" />
                  <span>Loading tags…</span>
                </div>
              ) : tagSearchMatches.length === 0 ? (
                <div className="ai-analysis__empty">
                  {hasActiveSearchQuery(tagSearchQuery)
                    ? 'No tags match this search.'
                    : 'No tags for this field yet.'}
                </div>
              ) : (
                <div className="ai-analysis__combine-match-scroll">
                  {tagSearchMatches.map((v) => {
                    const selected =
                      tagActionMode === 'split' ? isInSplitPool(v.key) : isInReplacePool(v.value);
                    return (
                      <div key={v.key} className="ai-analysis__combine-match-row">
                        <div className="ai-analysis__combine-match-text">
                          <span className="ai-analysis__word-map-value">{v.value}</span>
                          <span className="ai-analysis__word-map-count">
                            {v.product_count} product{v.product_count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="ai-analysis__combine-match-actions">
                          <button
                            type="button"
                            className="ai-analysis__btn ai-analysis__btn--secondary"
                            disabled={!canWrite || selected}
                            onClick={() => selectTagResult(v)}
                          >
                            {selected ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tagActionMode === 'replace' && replacePool.length > 0 && (
                <div className="ai-analysis__combine-pool">
                  <span className="ai-analysis__suggestion-label-text">Replace pool</span>
                  <div className="ai-analysis__keyword-pills">
                    {replacePool.map((v) => (
                      <span key={v} className="ai-analysis__keyword-pill">
                        {v}
                        <button
                          type="button"
                          className="ai-analysis__keyword-pill-remove"
                          aria-label={`Remove ${v}`}
                          disabled={!canWrite}
                          onClick={() => removeFromReplacePool(v)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <label className="ai-analysis__suggestion-label">
                    Canonical label
                    <input
                      type="text"
                      className="ai-analysis__cluster-canonical"
                      value={replaceCanonical}
                      onChange={(e) => setReplaceCanonical(e.target.value)}
                      disabled={!canWrite}
                      placeholder="e.g. lifestyle, vintage, screen-print"
                    />
                  </label>
                  <div className="ai-analysis__suggestion-actions">
                    <button
                      type="button"
                      className="ai-analysis__btn"
                      disabled={!canWrite || stdApplyLoading}
                      onClick={applyReplace}
                    >
                      {stdApplyLoading ? 'Applying…' : 'Apply replace'}
                    </button>
                  </div>
                </div>
              )}

              {tagActionMode === 'split' && splitPool.length > 0 && (
                <div className="ai-analysis__combine-pool ai-analysis__combine-pool--split">
                  <span className="ai-analysis__suggestion-label-text">Split pool</span>
                  <div className="ai-analysis__keyword-pills">
                    {splitPool.map((t) => (
                      <span key={t.key} className="ai-analysis__keyword-pill">
                        {t.value}
                        <span className="ai-analysis__keyword-pill-meta">
                          ({t.product_count} product{t.product_count === 1 ? '' : 's'})
                        </span>
                        <button
                          type="button"
                          className="ai-analysis__keyword-pill-remove"
                          aria-label={`Remove ${t.value}`}
                          disabled={!canWrite}
                          onClick={() => removeFromSplitPool(t.key)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="ai-analysis__sub">
                    Replacement tags apply to every tag in the split pool.
                  </p>
                  {splitReplacements.map((val, i) => (
                    <label key={i} className="ai-analysis__suggestion-label">
                      Tag {i + 1}
                      <input
                        type="text"
                        className="ai-analysis__cluster-canonical"
                        value={val}
                        onChange={(e) =>
                          setSplitReplacements((prev) => {
                            const next = [...prev];
                            next[i] = e.target.value;
                            return next;
                          })
                        }
                        disabled={!canWrite}
                      />
                    </label>
                  ))}
                  <div className="ai-analysis__suggestion-actions">
                    <button
                      type="button"
                      className="ai-analysis__btn ai-analysis__btn--secondary"
                      disabled={!canWrite}
                      onClick={() => setSplitReplacements((prev) => [...prev, ''])}
                    >
                      Add tag
                    </button>
                    <button
                      type="button"
                      className="ai-analysis__btn ai-analysis__btn--secondary"
                      disabled={!canWrite || !splitPool.length}
                      onClick={autoSplitFill}
                    >
                      Auto-split
                    </button>
                    <button
                      type="button"
                      className="ai-analysis__btn"
                      disabled={!canWrite || stdApplyLoading}
                      onClick={applySplitPool}
                    >
                      {stdApplyLoading ? 'Applying…' : `Apply split (${splitPool.length})`}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
